import React, { useState, useEffect } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import axios from 'axios';
import { API_BASE_URL } from '../config';
import { formatCurrency } from '../utils/formatters';

const MinigameQRScanner = ({ userId, minigameId, onSuccess, onError }) => {
  const [scanning, setScanning] = useState(false);
  const [scanner, setScanner] = useState(null);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [minigameInfo, setMinigameInfo] = useState(null);
  const [activated, setActivated] = useState(false);
  
  // Fetch minigame info
  useEffect(() => {
    const fetchMinigameInfo = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/api/minigames/${minigameId}`);
        setMinigameInfo(response.data);
      } catch (err) {
        console.error('Error fetching minigame info:', err);
        setError('Could not load minigame information');
      }
    };
    
    fetchMinigameInfo();
  }, [minigameId]);
  
  // Initialize scanner
  useEffect(() => {
    if (scanning && !scanner) {
      try {
        const html5QrCode = new Html5Qrcode("minigame-qr-reader");
        setScanner(html5QrCode);
        
        const qrConfig = {
          fps: 10,
          qrbox: 250,
          aspectRatio: 1.0,
        };
        
        html5QrCode.start(
          { facingMode: "environment" },
          qrConfig,
          onScanSuccess,
          onScanFailure
        )
        .catch(err => {
          console.error("Error starting scanner:", err);
          setError("Could not start camera scanner. Please check permissions.");
          setScanning(false);
        });
      } catch (err) {
        console.error("Error initializing scanner:", err);
        setError("Could not initialize QR scanner");
        setScanning(false);
      }
    }
    
    return () => {
      if (scanner) {
        scanner.stop().catch(err => console.error("Error stopping scanner:", err));
      }
    };
  }, [scanning, scanner]);
  
  const startScanning = () => {
    setScanning(true);
    setError('');
    setMessage('');
  };
  
  const activateMinigame = async () => {
    try {
      setMessage('Activating minigame...');
      
      // Call API to activate minigame directly (without QR code)
      const response = await axios.post(`${API_BASE_URL}/api/minigames/activate`, {
        userId,
        minigameId
      });
      
      if (response.data.success) {
        setActivated(true);
        setMessage('Minigame activated! Go to the physical station to play.');
        
        if (onSuccess) {
          onSuccess(response.data);
        }
      } else {
        setError(response.data.message || 'Failed to activate minigame');
        
        if (onError) {
          onError(new Error(response.data.message));
        }
      }
    } catch (err) {
      console.error("Error activating minigame:", err);
      setError(err.response?.data?.message || 'Error activating minigame');
      
      if (onError) {
        onError(err);
      }
    }
  };
  
  const onScanSuccess = async (decodedText) => {
    // Pause scanner temporarily
    if (scanner) {
      try {
        await scanner.pause();
      } catch (error) {
        console.error("Error pausing scanner:", error);
      }
    }
    
    console.log("QR Code detected:", decodedText);
    setMessage('Processing QR code...');
    
    try {
      // Check if it's a minigame activation code (MINIGAME:{minigameId}:{timestamp})
      if (decodedText.startsWith('MINIGAME:')) {
        setMessage('Activating minigame...');
        
        // Extract minigame ID from QR code
        const parts = decodedText.split(':');
        if (parts.length >= 3) {
          const scannedMinigameId = parts[1];
          
          // Call API to activate minigame
          const response = await axios.post(`${API_BASE_URL}/api/minigames/activate`, {
            userId,
            minigameId: scannedMinigameId
          });
          
          if (response.data.success) {
            setActivated(true);
            setSuccess(true);
            setMessage('Minigame activated! Go to the physical station to play.');
            
            // Call success callback
            if (onSuccess) {
              onSuccess(response.data);
            }
          } else {
            setError(response.data.message || 'Failed to activate minigame');
            resumeScanner();
          }
        } else {
          setError('Invalid minigame QR code format');
          resumeScanner();
        }
      }
      // Check if it's a reward QR code (REWARD:{minigameId}:{amount}:{timestamp})
      else if (decodedText.startsWith('REWARD:')) {
        setMessage('Processing reward...');
        
        // Extract data from QR code
        const parts = decodedText.split(':');
        if (parts.length >= 4) {
          const scannedMinigameId = parts[1];
          const reward = parseFloat(parts[2]);
          
          // Call API to claim reward
          const response = await axios.post(`${API_BASE_URL}/api/minigames/claim-reward`, {
            userId,
            minigameId: scannedMinigameId,
            qrCode: decodedText
          });
          
          if (response.data.success) {
            setSuccess(true);
            setMessage(`Congratulations! You won ${formatCurrency(response.data.reward)}`);
            
            // Call success callback
            if (onSuccess) {
              onSuccess(response.data);
            }
          } else {
            setError(response.data.message || 'Failed to claim reward');
            resumeScanner();
          }
        } else {
          setError('Invalid reward QR code format');
          resumeScanner();
        }
      }
      // Process the existing result QR code format (RESULT-{minigameId}-{result}-{amount})
      else if (decodedText.startsWith(`RESULT-`)) {
        setMessage('Processing result...');
        
        // Parse the result code
        const parts = decodedText.split('-');
        if (parts.length >= 4) {
          const scannedMinigameId = parts[1];
          const resultType = parts[2]; // 'win' or 'loss'
          const amount = parseFloat(parts[3]);
          
          if (resultType === 'win') {
            // Call API to process win
            const response = await axios.post(`${API_BASE_URL}/api/minigames/process-result`, {
              userId,
              minigameId: scannedMinigameId,
              result: 'win',
              amount,
              qrCode: decodedText
            });
            
            if (response.data.success) {
              setSuccess(true);
              setMessage(`Congratulations! You won ${formatCurrency(amount)}`);
              
              if (onSuccess) {
                onSuccess(response.data);
              }
            } else {
              setError(response.data.message || 'Failed to process win');
              resumeScanner();
            }
          } else {
            setMessage('Better luck next time!');
            setTimeout(() => {
              setScanning(false);
              setActivated(false);
            }, 2000);
          }
        } else {
          setError('Invalid result QR code format');
          resumeScanner();
        }
      } else {
        setError('Invalid QR code. Please scan a valid minigame code.');
        resumeScanner();
      }
    } catch (err) {
      console.error("Error processing QR code:", err);
      setError(err.response?.data?.message || 'Error processing QR code');
      resumeScanner();
      
      if (onError) {
        onError(err);
      }
    }
  };
  
  const resumeScanner = () => {
    if (scanner && !success) {
      setTimeout(() => {
        scanner.resume().catch(err => {
          console.error("Error resuming scanner:", err);
        });
      }, 2000);
    }
  };
  
  const onScanFailure = (error) => {
    // Handle scan failure silently - no need to show to user
    console.warn("QR scan error:", error);
  };
  
  return (
    <div className="minigame-qr-scanner">
      {error && (
        <div className="error-message">
          <p>{error}</p>
          <button 
            className="try-again-button"
            onClick={() => {
              setError('');
              setScanning(false);
            }}
          >
            Try Again
          </button>
        </div>
      )}
      
      {success && (
        <div className="success-message">
          <p>{message}</p>
          <button 
            className="done-button"
            onClick={() => {
              if (onSuccess) {
                onSuccess({ completed: true });
              }
            }}
          >
            Done
          </button>
        </div>
      )}
      
      {/* Main flow */}
      {!error && !success && (
        <div className="minigame-flow">
          {!activated ? (
            <div className="activation-step">
              <p>Ready to play {minigameInfo?.name}?</p>
              <p>This will cost {formatCurrency(minigameInfo?.cost)} from your balance.</p>
              
              <button 
                className="activate-button"
                onClick={activateMinigame}
              >
                Pay & Play
              </button>
            </div>
          ) : (
            <div className="result-step">
              {message && <p className="status-message">{message}</p>}
              
              <div className="scanner-instructions">
                <p>After you finish playing the physical game, scan the result QR code.</p>
              </div>
              
              {scanning ? (
                <div className="active-scanner">
                  <div id="minigame-qr-reader"></div>
                  <button className="cancel-scan" onClick={() => setScanning(false)}>
                    Cancel
                  </button>
                </div>
              ) : (
                <button 
                  className="scan-result-button" 
                  onClick={startScanning}
                >
                  Scan Result QR Code
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MinigameQRScanner;